// src/screens/MilestoneTracker.js — Milestones, console-standard redesign
// (June 2026). Same live metrics + localStorage overrides as before, restyled
// onto Console Panel/theme tokens so it matches the rest of the consoles in
// both light and dark themes.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, Panel, Empty, useConsoleTheme, F } from '../components/Console';
import { supabase } from '../supabaseClient';

const PRE_LAUNCH = [
  { id:'geo_50',    label:'Geocoding > 50% coverage',          target:50,   unit:'%', metric:'geocoding' },
  { id:'geo_cam',   label:'Cambodia fully geocoded',            target:80,   unit:'%', metric:'geocoding_kh' },
  { id:'sana_95',   label:'Sana result rate > 95%',            target:95,   unit:'%', metric:'sana' },
  { id:'verif_30',  label:'30% of locations verified',         target:30,   unit:'%', metric:'verified' },
  { id:'qa_90',     label:'QA pass rate > 90%',                target:90,   unit:'%', metric:'qa' },
  { id:'tickets_0', label:'Zero open support tickets',         target:0,    unit:'open', metric:'tickets', lowerIsBetter:true },
  { id:'tags_clean',label:'Tag normalizer — zero flagged',     target:0,    unit:'issues', metric:'tag_issues', lowerIsBetter:true },
  { id:'beta_100',  label:'100 beta users onboarded',          target:100,  unit:'users', metric:'users' },
];

const POST_LAUNCH = [
  { id:'nps_50',    label:'NPS > 50',                          target:50,   unit:'', metric:'nps' },
  { id:'pass_50',   label:'50 passes sold',                    target:50,   unit:'passes', metric:'passes' },
  { id:'mrr_1k',    label:'$1,000 MRR',                        target:1000, unit:'$', metric:'mrr', prefix:'$' },
  { id:'geo_80',    label:'Geocoding > 80% globally',          target:80,   unit:'%', metric:'geocoding' },
  { id:'sana_98',   label:'Sana result rate > 98%',            target:98,   unit:'%', metric:'sana' },
  { id:'users_500', label:'500 registered users',              target:500,  unit:'users', metric:'users' },
  { id:'countries', label:'Data in 40+ countries',             target:40,   unit:'countries', metric:'countries' },
  { id:'verif_60',  label:'60% locations verified',            target:60,   unit:'%', metric:'verified' },
];

function MilestoneTrackerBody() {
  const { t } = useConsoleTheme();
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sanoire_milestone_overrides')||'{}'); } catch (_e) { return {}; }
  });

  useEffect(() => {
    (async () => {
      try {
        const [
          {count:total},{count:geocoded},{count:verified},{count:totalKH},{count:geocodedKH},
          {count:passes},{count:openTickets},{data:sanaData},{count:countries},
        ] = await Promise.all([
          supabase.from('Locations').select('*',{count:'exact',head:true}),
          supabase.from('Locations').select('*',{count:'exact',head:true}).not('latitude','is',null),
          supabase.from('Locations').select('*',{count:'exact',head:true}).gte('last_verified',(new Date(Date.now()-365*86400000)).toISOString().slice(0,10)),
          supabase.from('Locations').select('*',{count:'exact',head:true}).eq('country','Cambodia'),
          supabase.from('Locations').select('*',{count:'exact',head:true}).eq('country','Cambodia').not('latitude','is',null),
          supabase.from('user_passes').select('*',{count:'exact',head:true}),
          supabase.from('support_tickets').select('*',{count:'exact',head:true}).eq('status','open'),
          supabase.from('sana_invocations').select('fallback_level').order('created_at',{ascending:false}).limit(300),
          supabase.rpc('get_ungeocoded_by_country'),
        ]);
        const sanaRows = sanaData||[];
        const sanaRate = sanaRows.length>0 ? Math.round((sanaRows.filter(r=>r.fallback_level<3).length/sanaRows.length)*100) : 0;
        const countryCount = Array.isArray(countries) ? countries.length : (countries||0);
        const m = {
          geocoding:    total>0?Math.round(geocoded/total*100):0,
          geocoding_kh: totalKH>0?Math.round(geocodedKH/totalKH*100):0,
          sana:         sanaRate,
          verified:     total>0?Math.round(verified/total*100):0,
          qa:           84,
          tickets:      openTickets||0,
          tag_issues:   0,
          users:        0,
          nps:          62,
          passes:       passes||0,
          mrr:          (passes||0)*14,
          countries:    countryCount>0?countryCount:31,
        };
        setMetrics(m);
      } catch (_e) {}
      setLoading(false);
    })();
  }, []);

  const getValue = (ms) => {
    if (overrides[ms.id] !== undefined) return parseFloat(overrides[ms.id]);
    return metrics[ms.metric] ?? null;
  };

  const getMilestoneStatus = (ms, val) => {
    if (val === null) return { pct: 0, done: false, color: t.mut };
    const pct = ms.lowerIsBetter
      ? (val <= ms.target ? 100 : Math.max(0, 100 - (val / Math.max(ms.target, 1)) * 50))
      : Math.min(100, ms.target === 0 ? (val === 0 ? 100 : 0) : (val / ms.target) * 100);
    const done = ms.lowerIsBetter ? val <= ms.target : val >= ms.target;
    const color = done ? t.up : pct >= 60 ? t.warn : t.danger;
    return { pct: Math.round(pct), done, color };
  };

  const renderSection = (title, milestones) => {
    const doneCt = milestones.filter(ms => getMilestoneStatus(ms, getValue(ms)).done).length;
    return (
      <Panel title={title} style={{ marginBottom: 14 }}
        right={<span style={{ fontSize:'0.82rem', fontWeight:500, color: doneCt===milestones.length ? t.up : t.mut }}>{doneCt}/{milestones.length} complete</span>}>
        {milestones.map(ms => {
          const val = getValue(ms);
          const {pct,done,color} = getMilestoneStatus(ms, val);
          return (
            <div key={ms.id} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4,gap:10}}>
                <div style={{display:'flex',alignItems:'center',gap:7,minWidth:0}}>
                  <span style={{fontSize:13,color:done?t.up:t.mut,flexShrink:0}}>{done?'✓':'○'}</span>
                  <span style={{fontSize:'0.88rem',fontWeight:done?500:400,color:done?t.up:t.tx,textDecoration:done?'line-through':'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ms.label}</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                  <input
                    type="number"
                    value={overrides[ms.id]!==undefined?overrides[ms.id]:(val??'')}
                    onChange={e=>{
                      const next={...overrides,[ms.id]:e.target.value};
                      setOverrides(next);
                      localStorage.setItem('sanoire_milestone_overrides',JSON.stringify(next));
                    }}
                    placeholder="—"
                    style={{width:56,padding:'3px 7px',borderRadius:6,border:`1px solid ${t.bd}`,background:t.period,color:t.tx,fontSize:'0.8rem',fontFamily:'monospace',textAlign:'right',outline:'none'}}
                  />
                  <span style={{fontSize:'0.74rem',color:t.mut,minWidth:24,fontVariantNumeric:'tabular-nums'}}>{ms.prefix||''}{ms.target}{ms.unit}</span>
                </div>
              </div>
              <div style={{height:4,borderRadius:2,background:t.gbar,overflow:'hidden'}}>
                <div style={{height:'100%',width:pct+'%',background:color,borderRadius:2,transition:'width .4s'}}/>
              </div>
            </div>
          );
        })}
      </Panel>
    );
  };

  return (
    <div style={{ fontFamily: F }}>
      <div style={{ fontSize:'0.9rem', color:t.mut, margin:'2px 0 16px', lineHeight:1.5 }}>
        Track pre-launch and post-launch goals. Values auto-populate from live data — click any number to manually override.
      </div>
      {loading && <Empty>Loading live metrics…</Empty>}
      {renderSection('Pre-launch milestones', PRE_LAUNCH)}
      {renderSection('Post-launch milestones', POST_LAUNCH)}
    </div>
  );
}

export default function MilestoneTracker(props) {
  if (props && props.embed) return <MilestoneTrackerBody {...props} />;
  return <ConsoleShell title="Milestones"><MilestoneTrackerBody {...props} /></ConsoleShell>;
}
